import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  Box,
  Button,
  Collapse,
  Group,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import {
  ChevronDown,
  Table as TableIcon,
} from 'lucide-react'
import useWebSocket from '@hooks/useWebSocket'
import { useVisibleDevices } from '@data/device/device'
import type Device from '@data/device/model/device'
import { DeviceIDTypes } from '@data/device/model/device'
import backend from '@api/backend'
import { notifyApiError } from '@common/utils/notify'
import RealTimeFlowGauge from './RealTimeFlowGauge'
import RealTimeFlowTable, { type FlowReading } from './RealTimeFlowTable'

import './RealTimeFlow.scss'

interface RtfPayload {
  message_id: string
  status: number
  payload: {
    Flow: number
    device_id: string
    enqueued_time: string
    messageType: string
  }
}

const parseRtfPayload = (data: unknown): RtfPayload | undefined => {
  if (typeof data !== 'string') return undefined

  try {
    return JSON.parse(data) as RtfPayload
  } catch (error) {
    console.warn('Invalid RTF websocket message', error)
    return undefined
  }
}

function Dashboard() {
  const [socketUrl, setSocketUrl] = useState<string | null>(null)
  const [queuedSocketUrl, setQueuedSocketUrl] = useState<string | null>(null)
  const [flowValue, setFlowValue] = useState<number>()
  const [flowStatus, setFlowStatus] = useState<number>()
  const [flowReadings, setFlowReadings] = useState<FlowReading[]>([])
  const [maxValue, setMaxValue] = useState(30)
  const [tableVisible, setTableVisible] = useState(false)
  const allDevices = useVisibleDevices()
  const { lastMessage, readyState, getWebSocket } = useWebSocket(socketUrl, {
    // onOpen: () => console.log('opened'),
    onClose: (event) => closeWS(event),
  }, Boolean(socketUrl))
  const [devices, setDevice] = useState<Device[]>()
  const [selectedDevice, setSelectedDevice] = useState<Device>()
  const [rtfRunning, setRtfRunning] = useState<boolean>(false)

  const deviceOptions = useMemo(() => (devices ?? []).map((device) => ({
    value: device.deviceId,
    label: device.deviceName,
  })), [devices])
  const selectedDeviceId = selectedDevice?.deviceId ?? null

  const connType = import.meta.env.VITE_APP_API_URI!.includes('localhost') ? 'ws' : 'wss'
  const wsURL = import.meta.env.VITE_APP_API_URI!.replace(/^https?:\/\//, '')
  const appEnvState = import.meta.env.VITE_APP_ENV_STATE

  const closeWS = (event: WebSocketEventMap['close']) => {
    // console.log(event)
  }

  const resetFlowReading = useCallback(() => {
    setFlowValue(undefined)
    setFlowStatus(undefined)
    setFlowReadings([])
  }, [])

  const updateFlowReading = useCallback((nextValue?: number, nextStatus?: number, nextTime?: string) => {
    setFlowValue(nextValue)
    setFlowStatus(nextStatus)

    setMaxValue((currentMaxValue) => (
      nextValue && nextValue > currentMaxValue
        ? nextValue * 1.2
        : currentMaxValue
    ))

    if (nextValue !== undefined) {
      setFlowReadings((currentReadings) => [
        ...currentReadings,
        { time: nextTime ?? new Date().toISOString(), value: nextValue },
      ])
    }
  }, [])

  const averageFlowValue = useMemo(() => (
    flowReadings.length
      ? flowReadings.reduce((sum, reading) => sum + reading.value, 0) / flowReadings.length
      : undefined
  ), [flowReadings])

  const handleSelectedDeviceIdChange = useCallback((deviceId: string | null) => {
    setSelectedDevice(devices?.find((device) => device.deviceId === deviceId))
  }, [devices])

  const connectSocket = (newSocketUrl: string) => {
    if (newSocketUrl === socketUrl) {
      getWebSocket()?.close()
      setSocketUrl(null)
      setQueuedSocketUrl(newSocketUrl)
      return
    }

    setSocketUrl(newSocketUrl)
  }

  useEffect(() => {
    setDevice(allDevices.filter(d => d.deviceVendor === 'SMARTFLOW' && d.model === '3V004'))
  }, [allDevices])

  useEffect(() => {
    if (socketUrl || !queuedSocketUrl) return

    setSocketUrl(queuedSocketUrl)
    setQueuedSocketUrl(null)
  }, [queuedSocketUrl, socketUrl])

  useEffect(() => {
    const message = parseRtfPayload(lastMessage?.data)
    if (!message) return

    const { status, payload } = message
    if (status === 1) {
      const { messageType, Flow: flow, enqueued_time: enqueuedTime } = payload
      updateFlowReading(flow / 1000, status, enqueuedTime)
      if (messageType === 'END_RTF') {
        setRtfRunning(false)
        getWebSocket()?.close()
      }
    } else {
      updateFlowReading(undefined, status)
    }
  }, [getWebSocket, lastMessage, updateFlowReading])

  useEffect(() => {
    if (selectedDevice) {
      setRtfRunning(false)
      resetFlowReading()
      setMaxValue(30)
    }
    getWebSocket()?.close()
  }, [getWebSocket, resetFlowReading, selectedDevice])

  useEffect(() => {
    if (readyState === 3) {
      setRtfRunning(false)
    }
  }, [readyState])

  const go = () => {
    resetFlowReading()
    if (!selectedDevice) return

    if (appEnvState === 'demo') {
      const newSocketUrl: string = `${connType}://${wsURL}/devices/${selectedDevice.dUuid}/rtf_demo?idType=${DeviceIDTypes.dUuid}`
      connectSocket(newSocketUrl)
      setRtfRunning(true)
    } else {
      setRtfRunning(true)
      backend
        .get<{ token: string }>(`devices/${selectedDevice.dUuid}/rtf_token`, {
          params: { idType: DeviceIDTypes.dUuid },
        })
        .then((resp) => {
          const {
            data: { token },
          } = resp
          setFlowValue(undefined)
          setFlowStatus(2)
          const newSocketUrl: string = `${connType}://${wsURL}/devices/${selectedDevice.dUuid}/rtf?token=${token}&idType=${DeviceIDTypes.dUuid}`
          connectSocket(newSocketUrl)
        })
        .catch((error) => {
          setRtfRunning(false)
          setSocketUrl(null)
          resetFlowReading()
          notifyApiError(error, 'Unable to start real time flow')
        })
    }
  }

  return (
    <Box className={'rtf-management'}>
      <Title order={1}>Real Time Flow</Title>

      <Stack mt={'md'} gap={'md'}>
        <Stack gap={'xs'}>
          <Group gap={'sm'} align={'baseline'}>
            <Text fw={500}>Device</Text>
            <Text c={'red'} size={'sm'}>
              **RTF function is only available on SMARTFLOW hubs.
            </Text>
          </Group>

          <Select
            styles={{ dropdown: { zIndex: 2 } }}
            w={{ base: '100%', md: 360 }}
            value={selectedDeviceId}
            onChange={handleSelectedDeviceIdChange}
            data={deviceOptions}
            name={'device'}
            clearable={false}
            searchable
            placeholder={'Select a device'}
            disabled={rtfRunning}
          />
        </Stack>

        {selectedDevice ? (
          <>
            <Stack
              className={'rtf-view-container'}
              gap={'md'}
              align={'stretch'}
            >
              <RealTimeFlowGauge
                value={flowValue}
                status={flowStatus}
                maxValue={maxValue}
                averageValue={averageFlowValue}
              />
              <Button
                disabled={rtfRunning}
                loading={rtfRunning}
                onClick={go}
              >
                Start RTF
              </Button>

              <Group
                justify={'space-between'}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setTableVisible((visible) => !visible)}
              >
                <Group gap={'xs'}>
                  <TableIcon size={16} />
                  <Text size={'sm'} fw={500}>
                    {tableVisible ? 'Hide' : 'Show'} Flow Data
                  </Text>
                </Group>

                <ChevronDown
                  size={16}
                  style={{
                    color: 'var(--mantine-color-dimmed)',
                    transform: tableVisible ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </Group>
            </Stack>

            <Collapse expanded={tableVisible}>
              <RealTimeFlowTable readings={flowReadings} />
            </Collapse>
          </>
        ) : (
          <Text mt={'md'} fs={'italic'} c={'dimmed'}>
            Select a device
          </Text>
        )}
      </Stack>
    </Box>
  )
}

export default Dashboard
